import React from 'react';
import type { ButtonHTMLAttributes } from 'react';
import omit from 'lodash/omit';

import type { RulesExtend } from '@ackee/fela';
import { useFelaEnhanced } from '@ackee/fela';
import { LocalizedLink, type LocalizedLinkProps } from '../LocalizedLink';
import { ButtonSize, ButtonColor, ButtonType } from './Button.properties';

import * as felaRules from './Button.rules';

const forbiddenAttrs = ['className', 'color', 'size', 'type', 'extend'] as const;
type ForbiddenAttrs = typeof forbiddenAttrs[number];

interface BaseButtonProps {
    size?: ButtonSize;
    color?: ButtonColor;
    extend?: RulesExtend<typeof felaRules>;
}

export interface NativeButtonProps
    extends BaseButtonProps,
        Omit<ButtonHTMLAttributes<HTMLButtonElement>, ForbiddenAttrs> {
    type?: ButtonType.BUTTON;
}

export interface LinkButtonProps extends BaseButtonProps, Omit<LocalizedLinkProps, ForbiddenAttrs> {
    type: ButtonType.LINK;
}

export type ButtonProps = NativeButtonProps | LinkButtonProps;

export const Button = (props: ButtonProps) => {
    const { size = ButtonSize.MEDIUM, color = ButtonColor.BLUE, extend } = props;
    const { styles } = useFelaEnhanced(felaRules, { size, color, extend });

    if (props.type === ButtonType.LINK) {
        const linkProps = omit(props, forbiddenAttrs) as Omit<LinkButtonProps, ForbiddenAttrs>;

        return <LocalizedLink {...linkProps} className={styles.button} />;
    }

    const buttonProps = omit(props, forbiddenAttrs) as Omit<NativeButtonProps, ForbiddenAttrs>;

    return <button {...buttonProps} className={styles.button} />;
};

Button.Type = ButtonType;
